/**
 * Shared API hooks — TanStack Query wrappers for SafetyNow API.
 * Satisfies AGENT-001 §5: Server state = TanStack Query.
 *
 * CANONICAL SOURCE — all frontend API calls MUST use the helpers exported
 * from this file (apiFetch, authHeaders, API_BASE). See
 * .kiro/steering/frontend-conventions.md for the full guidance.
 */
import { QueryClient, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';

// ── Canonical API base path ───────────────────────────────────────────────

/** API base path — do NOT duplicate this in other files. Import it. */
export const API_BASE = '/api/v1';

// ── QueryClient singleton ──────────────────────────────────────────────────

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
    mutations: { retry: 0 },
  },
});

// ── Auth headers ───────────────────────────────────────────────────────────

export function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('sn:auth-token');
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
}

// ── Base fetch with auth ───────────────────────────────────────────────────

export async function apiFetch<T = unknown>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { ...authHeaders(), ...init?.headers },
    signal: init?.signal ?? AbortSignal.timeout(10_000),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, body?.error?.message || res.statusText, body?.error);
  }
  const data = await res.json();
  return (data?.data !== undefined ? data.data : data) as T;
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public detail?: unknown) {
    super(message);
  }
}

// ── Generic query hook ─────────────────────────────────────────────────────

export function useApiQuery<T>(
  key: (string | undefined)[],
  path: string,
  opts?: Omit<UseQueryOptions<T, ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<T, ApiError>({
    queryKey: key,
    queryFn: () => apiFetch<T>(path),
    enabled: key.every(k => k !== undefined),
    ...opts,
  });
}

// ── Generic mutation hook ──────────────────────────────────────────────────

export function useApiMutation<TData = unknown, TVariables = unknown>(
  method: 'POST' | 'PUT' | 'DELETE',
  path: string | ((vars: TVariables) => string),
  opts?: Omit<UseMutationOptions<TData, ApiError, TVariables>, 'mutationFn'>,
) {
  const _qc = useQueryClient();
  return useMutation<TData, ApiError, TVariables>({
    mutationFn: async (vars) => {
      const url = typeof path === 'function' ? path(vars) : path;
      return apiFetch<TData>(url, {
        method,
        body: method !== 'DELETE' ? JSON.stringify(vars) : undefined,
      });
    },
    onSuccess: (...args) => {
      opts?.onSuccess?.(...args);
    },
    ...opts,
  });
}

// ── Invalidation helper ────────────────────────────────────────────────────

export function useInvalidate() {
  const qc = useQueryClient();
  return (...keys: string[]) => qc.invalidateQueries({ queryKey: keys });
}
